import React, { useCallback, useMemo, useState } from "react";
import { useGameIdFromRoute, usePublisherIdFromRoute } from "../WithGameIdFromRoute";
import { NoItemsFound } from "../../publisher/NoItemsFound";
import Alert from "@mui/material/Alert";
import { AddCustomPublisherDialog } from "./AddCustomPublisherDialog";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { PublisherLogoSquare2 } from "../../publisher/PublisherLogo";
import { PublisherBannerIcon } from "../../publishers/ListItem";
import { useNavigate } from "react-router-dom";
import { Path } from "../../Path";
import { useGamePublishers, useShortlistPublisher } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import { PublisherResearchSummaryBody } from "./PublisherResearchSummaryBody";
import { ResearchPublisherFitTierChip } from "./ResearchPublisherFitTierChip";
import { ShortlistPublisherFilterProvider } from "./ShortlistPublisherFilter";
import { PublisherNameSearchField } from "./PublisherNameSearchField";
import { PublisherResearchListView } from "./usePublisherResearchListView";
import Box from "@mui/material/Box";

const NoPublishersFound = () => (
  <NoItemsFound>
    <Alert severity="info">Publishers available for research will show here.</Alert>
  </NoItemsFound>
);

const NoMatchingPublishers = () => (
  <NoItemsFound>
    <Alert severity="info">No publishers match your search.</Alert>
  </NoItemsFound>
);

/**
 * @typedef {{
 *   activePublisherId: import("@seabrookstudios/pitch2table-core").PublisherId | null,
 *   openPublisher: (id: import("@seabrookstudios/pitch2table-core").PublisherId) => void,
 *   shortlistedPublisherIds: Set<import("@seabrookstudios/pitch2table-core").PublisherId>,
 * }} PublisherResearchUiValue
 */

/** @type {React.Context<PublisherResearchUiValue>} */
const PublisherResearchUiContext = React.createContext(
  /** @type {PublisherResearchUiValue} */ ({
    activePublisherId: null,
    openPublisher: () => {},
    shortlistedPublisherIds: new Set(),
  })
);

/**
 * @param {Object} props
 * @param {[import("@seabrookstudios/pitch2table-core").PublisherForApp, import("@seabrookstudios/pitch2table-core").ContactBookPublisher|undefined, number|undefined, string|undefined]} props.item
 * @param {import("react").CSSProperties} [props.style]
 */
const ResearchPublisherListEntry = ({ item, style }) => {
  const publisher = /** @type {import("@seabrookstudios/pitch2table-core").PublisherForApp} */ (item[0]);
  const score = item[2];
  const tier = item[3];
  const { activePublisherId, openPublisher, shortlistedPublisherIds } = React.useContext(PublisherResearchUiContext);
  const selected = activePublisherId === publisher.id;
  const isShortlisted = shortlistedPublisherIds.has(publisher.id);

  const row = (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={() => openPublisher(publisher.id)}>
      <ListItemAvatar>
        <PublisherLogoSquare2 publisher={publisher} width={48} height={48} />
      </ListItemAvatar>
      <ListItemText primary={publisher.name} />
      <ListItemIcon>
        <PublisherBannerIcon publisher={publisher} />
      </ListItemIcon>
      {isShortlisted && (
        <ListItemIcon>
          <Tooltip title="Shortlisted">
            <CheckCircle color="success" aria-label="Shortlisted publisher" />
          </Tooltip>
        </ListItemIcon>
      )}
      <ListItemIcon>
        <ResearchPublisherFitTierChip tier={tier} score={score} />
      </ListItemIcon>
    </ListItemButton>
  );

  if (!style) {
    return row;
  }

  return <div style={style}>{row}</div>;
};

const PublisherFitNotice = () => (
  <Alert severity="info">
    <Typography>
      Results are prioritised using our Publisher Fit Analysis, an experimental tool that matches your pitch against our
      PublisherDNA profile. Because we match against a publisher&apos;s entire brand essence rather than a single game,
      a perfect score is rare; focus on the first matches as your highest-priority leads. Think of this as a compass.
      <br />
      <br />
      Publisher Fit Analysis is a starting point. Always verify a publisher&apos;s current status and specific
      submission guidelines on their official website before reaching out.
    </Typography>
  </Alert>
);

const PublisherResearchListHeader = () => (
  <>
    <Box p={1}>
      <PublisherNameSearchField id="publisher-research-filter" />
    </Box>
    <PublisherFitNotice />
  </>
);

export const PublisherResearchListAndDetail = () => {
  const gameId = useGameIdFromRoute();
  const navigate = useNavigate();
  const publisherIdFromRoute = usePublisherIdFromRoute();
  const shortlistPublishers = useShortlistPublisher(gameId);
  const { data: gamePublishers = [] } = useGamePublishers(gameId);
  const routePublisherActive = Boolean(publisherIdFromRoute);

  const [modalPublisherId, setModalPublisherId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId | null} */ (null)
  );

  const activePublisherId = modalPublisherId ?? (routePublisherActive ? publisherIdFromRoute : null);

  const closePublisherModal = useCallback(() => {
    setModalPublisherId(null);
    if (routePublisherActive) {
      navigate(Path.game(gameId).research(), { replace: true });
    }
  }, [gameId, navigate, routePublisherActive]);

  const openPublisher = useCallback((id) => {
    setModalPublisherId(id);
  }, []);

  const shortlistedPublisherIds = useMemo(
    () => new Set(gamePublishers.map((publisher) => publisher.publisherId)),
    [gamePublishers]
  );

  const uiValue = useMemo(
    () => ({
      activePublisherId,
      openPublisher,
      shortlistedPublisherIds,
    }),
    [activePublisherId, openPublisher, shortlistedPublisherIds]
  );

  const shortlistActivePublisher = useCallback(async () => {
    if (!activePublisherId) {
      return;
    }

    await shortlistPublishers.mutateAsync({ publisherIds: [activePublisherId] });
  }, [activePublisherId]);

  return (
    <ShortlistPublisherFilterProvider>
      <PublisherResearchUiContext.Provider value={uiValue}>
        <PublisherResearchListView
          gameId={gameId}
          ListEntry={ResearchPublisherListEntry}
          ListHeader={PublisherResearchListHeader}
          EmptyList={NoPublishersFound}
          FilteredEmptyList={NoMatchingPublishers}
          Action={AddCustomPublisherDialog}
        />
        <FullScreenDialog
          id="publisher-research-summary"
          open={activePublisherId !== null}
          onClose={closePublisherModal}
          title="Publisher"
          actionText="Shortlist"
          onSaveHandler={shortlistActivePublisher}
          disabled={activePublisherId === null}
          isBusy={shortlistPublishers.isPending}
        >
          {activePublisherId !== null && <PublisherResearchSummaryBody publisherId={activePublisherId} />}
        </FullScreenDialog>
      </PublisherResearchUiContext.Provider>
    </ShortlistPublisherFilterProvider>
  );
};
